import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { PermissionGate } from "@/features/auth/components/PermissionGate";
import { storeCreditAPI, getApiErrorMessage } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { Price } from "@/components/ui/price";
import { useCurrency } from "@/hooks/useCurrency";
import { formatDate } from "@/lib/utils";

type Props = {
	customerId: number;
};

export function CustomerStoreCreditPanel({ customerId }: Props) {
	const { t } = useTranslation();
	const { toast } = useToast();
	const currency = useCurrency();
	const [balance, setBalance] = useState(0);
	const [transactions, setTransactions] = useState<
		Array<{
			id: number;
			type: string;
			amount: number;
			balanceAfter: number;
			createdAt: string;
			notes: string | null;
		}>
	>([]);
	const [issueOpen, setIssueOpen] = useState(false);
	const [adjustOpen, setAdjustOpen] = useState(false);
	const [issueAmount, setIssueAmount] = useState("");
	const [issueNotes, setIssueNotes] = useState("");
	const [adjustAmount, setAdjustAmount] = useState("");
	const [adjustNotes, setAdjustNotes] = useState("");
	const [saving, setSaving] = useState(false);

	const load = useCallback(async () => {
		const [balRes, txRes] = await Promise.all([
			storeCreditAPI.getBalance(customerId),
			storeCreditAPI.getCustomerTransactions(customerId, { page: 1, limit: 10 }),
		]);
		setBalance(Number(balRes.balance ?? 0));
		setTransactions(txRes.transactions ?? []);
	}, [customerId]);

	useEffect(() => {
		load().catch(() => undefined);
	}, [load]);

	const typeLabel = (type: string) => {
		const key = `checkoutPromotions.storeCredit.types.${type}`;
		const translated = t(key);
		return translated === key ? type : translated;
	};

	const handleIssue = async () => {
		const amount = Number(issueAmount);
		if (amount <= 0) return;
		try {
			setSaving(true);
			await storeCreditAPI.issue(customerId, { amount, notes: issueNotes || undefined });
			toast({ title: t("common.success"), description: t("checkoutPromotions.storeCredit.issued") });
			setIssueOpen(false);
			setIssueAmount("");
			setIssueNotes("");
			await load();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.storeCredit.errors.save")),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	const handleAdjust = async () => {
		const amount = Number(adjustAmount);
		if (!Number.isFinite(amount) || amount === 0) return;
		try {
			setSaving(true);
			await storeCreditAPI.adjust(customerId, { amount, notes: adjustNotes || undefined });
			toast({ title: t("common.success"), description: t("checkoutPromotions.storeCredit.adjusted") });
			setAdjustOpen(false);
			setAdjustAmount("");
			setAdjustNotes("");
			await load();
		} catch (error) {
			toast({
				title: t("common.error"),
				description: getApiErrorMessage(error, t, t("checkoutPromotions.storeCredit.errors.save")),
				variant: "destructive",
			});
		} finally {
			setSaving(false);
		}
	};

	return (
		<Card>
			<CardHeader className="flex flex-row items-center justify-between gap-4">
				<div className="flex flex-col gap-2">
					<CardTitle>{t("checkoutPromotions.storeCredit.customerTitle")}</CardTitle>
					<CardDescription>{t("checkoutPromotions.storeCredit.customerDescription")}</CardDescription>
				</div>
				<div className="flex gap-2">
					<PermissionGate permission="cashierStoreCredit.create">
						<Dialog open={issueOpen} onOpenChange={setIssueOpen}>
							<DialogTrigger asChild>
								<Button size="sm">{t("checkoutPromotions.storeCredit.issue")}</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>{t("checkoutPromotions.storeCredit.issue")}</DialogTitle>
								</DialogHeader>
								<div className="space-y-4 py-2">
									<div className="space-y-2">
										<Label>{t("common.amount")}</Label>
										<Input
											aria-label={t("common.amount")}
											type="number"
											min={0.01}
											step={0.01}
											value={issueAmount}
											onChange={e => setIssueAmount(e.target.value)}
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("common.notes")}</Label>
										<Input
											aria-label={t("common.notes")}
											value={issueNotes}
											onChange={e => setIssueNotes(e.target.value)}
										/>
									</div>
								</div>
								<DialogFooter>
									<Button variant="outline" onClick={() => setIssueOpen(false)}>
										{t("common.cancel")}
									</Button>
									<Button onClick={handleIssue} disabled={saving || Number(issueAmount) <= 0}>
										{t("common.save")}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</PermissionGate>
					<PermissionGate permission="cashierStoreCredit.update">
						<Dialog open={adjustOpen} onOpenChange={setAdjustOpen}>
							<DialogTrigger asChild>
								<Button size="sm" variant="outline">
									{t("checkoutPromotions.storeCredit.adjust")}
								</Button>
							</DialogTrigger>
							<DialogContent>
								<DialogHeader>
									<DialogTitle>{t("checkoutPromotions.storeCredit.adjust")}</DialogTitle>
								</DialogHeader>
								<div className="space-y-4 py-2">
									<div className="space-y-2">
										<Label>{t("checkoutPromotions.storeCredit.adjustAmount")}</Label>
										<Input
											aria-label={t("checkoutPromotions.storeCredit.adjustAmount")}
											type="number"
											step={0.01}
											value={adjustAmount}
											onChange={e => setAdjustAmount(e.target.value)}
											placeholder={t("common.adjustPlaceholder")}
										/>
									</div>
									<div className="space-y-2">
										<Label>{t("common.notes")}</Label>
										<Input
											aria-label={t("common.notes")}
											value={adjustNotes}
											onChange={e => setAdjustNotes(e.target.value)}
										/>
									</div>
								</div>
								<DialogFooter>
									<Button variant="outline" onClick={() => setAdjustOpen(false)}>
										{t("common.cancel")}
									</Button>
									<Button
										onClick={handleAdjust}
										disabled={saving || !adjustAmount || Number(adjustAmount) === 0}
									>
										{t("common.save")}
									</Button>
								</DialogFooter>
							</DialogContent>
						</Dialog>
					</PermissionGate>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<p className="text-2xl font-semibold">
					<Price value={balance} currency={currency} />
				</p>
				{transactions.length > 0 && (
					<div className="record-surface">
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("common.date")}</TableHead>
									<TableHead>{t("checkoutPromotions.coupons.type")}</TableHead>
									<TableHead>{t("common.amount")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{transactions.map(tx => (
									<TableRow key={tx.id}>
										<TableCell>{formatDate(tx.createdAt)}</TableCell>
										<TableCell>
											<Badge variant="outline">{typeLabel(tx.type)}</Badge>
										</TableCell>
										<TableCell>
											<Price value={tx.amount} currency={currency} />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</div>
				)}
			</CardContent>
		</Card>
	);
}
